import { BookMarked, Building2, FolderTree, Layers, Target } from 'lucide-react';
import { Breadcrumb } from '../../../../components/Breadcrumb';
import { SettingsMenu, type SettingsMenuEntry } from '../../../../components/settings/SettingsMenu';

/*
  What Investment Planning has to configure. One row per surface, each leading to its own page
  — the lists are deliberately not inlined here, because a reader opens Settings to find out
  what there is to configure and a page of stacked tables answers a different question.

  Every row belongs to a module with its own key, which is why the page itself is gated on
  holding ANY of them: one of them would hide it from a reader entitled to another.

  THE THREE GOAL RUNGS ARE THREE ROWS, not one. They share the `budget-goals.*` key set —
  maintaining the hierarchy is one administrative job — but each rung is a full list with its
  own columns, and a row leads to a full page. One row over a picker of three would be a second
  navigation step this rail already refuses to grow.

  They read TOP DOWN — Sectors, Sub-sectors, Goals — because that is the order a reader sets
  them up in and the order the hierarchy nests. The chart of accounts follows, because an
  account code is what a budget LINE draws on rather than part of the goal framework above it.
  (Fund Sources was a row here until 06 Oct 2026, when a PPA's fund source became its expense
  class — an enum, with nothing to maintain.)

  BUDGET DEPARTMENTS IS APPENDED LAST, and the position is a decision rather than where it
  happened to land. It is not part of the goal framework and it is not a vocabulary a line
  draws on: it names WHO proposes, which is a fact about the organization rather than about
  the budget's structure — and it is the one row here that points at another module's records
  rather than at a catalog of this one's. Appending also keeps the position of every row a
  reader has already learned.
*/
const ENTRIES: SettingsMenuEntry[] = [
  {
    key: 'goal-sectors',
    label: 'Sectors',
    description:
      'The broadest rung — the sectors an investment programme is organized under, e.g. Social or Infrastructure.',
    icon: Layers,
    to: '/procurement/investment-planning/settings/goal-sectors',
    permission: 'budget-goals.read',
  },
  {
    key: 'goal-sub-sectors',
    label: 'Sub-sectors',
    description: 'What each sector is broken down into, e.g. Health under Social.',
    icon: FolderTree,
    to: '/procurement/investment-planning/settings/goal-sub-sectors',
    permission: 'budget-goals.read',
  },
  {
    key: 'goals',
    label: 'Goals',
    description:
      'The goals a budget plan selects into its Goal Setting, each with the objective it serves and a strategy for reaching it.',
    icon: Target,
    to: '/procurement/investment-planning/settings/goals',
    permission: 'budget-goals.read',
  },
  {
    key: 'account-codes',
    label: 'Account Codes',
    description: 'The chart of accounts a budget line item is charged against.',
    icon: BookMarked,
    to: '/procurement/investment-planning/settings/account-codes',
    permission: 'budget-account-codes.read',
  },
  {
    key: 'budget-departments',
    label: 'Budget Departments',
    description:
      'Which organizational units propose a budget. Every plan lists one row per enrolled department, whether or not it has filed anything.',
    icon: Building2,
    to: '/procurement/investment-planning/settings/budget-departments',
    permission: 'budget-departments.read',
  },
];

/**
 * Procurement → Investment Planning → Settings: a menu of what the module group configures.
 *
 * An extension of the sidebar, which is what keeps the rail at one entry per module rather
 * than growing a row per configurable list.
 */
export function InvestmentPlanningSettingsPage() {
  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-50">
      <Breadcrumb segments={['Procurement', 'Investment Planning', 'Settings']} />

      <div className="modern-scrollbar min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto flex max-w-3xl flex-col gap-3">
          {/*
            One line, and it says what the menu cannot: these are what a budget plan and its
            PPAs are built FROM, so every one of them has to hold rows before either form can
            be submitted.
          */}
          <p className="text-xs text-slate-500">
            The goal framework an investment programme is organized under, and the accounts a
            budget line draws on. A budget plan and its PPAs are built from these.
          </p>

          <SettingsMenu entries={ENTRIES} />
        </div>
      </div>
    </div>
  );
}
